import Loading from 'components/common/Loading';
import {
  LOCAL_STORAGE,
  LOCAL_STORAGE_TRUE,
  USER_ROLES
} from 'constants/general';
import { ROUTES } from 'constants/routes';
import withAuth0 from 'hoc/withAuth0';
import { WithAuth0Props } from 'interfaces/auth.interface';
import { FC, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { resetPostHogToAnonymous } from 'services/posthog.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { routeManagerSelector } from 'store/reducer/selectors/navigation-routes.selector';
import { setIsUserAdmin, updateAuth0User } from 'store/reducer/user/slice';
import { getLocalStorage, updateLocalStorage } from 'utils/helpers/storage';
import { checkIfRedirectNeeded, randomLoadingMessage } from 'utils/routes.util';
import { protectedRoutes, publicRoutes } from './navigationRoutes';
import AuthPage from 'pages/auth';
import lazyDelayedImport from 'utils/lazyDelayedImport';
import { syncPlatformCourses } from 'store/reducer/setting/extra';
import ProtectedRoutes from './ProtectedRoutes';

const NotFoundPage = lazyDelayedImport(
  () => import('pages/other/NotFoundPage')
);
const ForbiddenPage = lazyDelayedImport(
  () => import('pages/other/ForbiddenPage')
);
const ErrorPage = lazyDelayedImport(() => import('pages/other/ErrorPage'));

const RouteManager: FC<WithAuth0Props> = ({
  authenticating,
  isAuthenticated,
  auth0User
}: WithAuth0Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { errorMessage } = useAppSelectorShallow(routeManagerSelector);
  // Track auth transitions to reset analytics on logout
  const wasAuthenticatedRef = useRef<boolean>(isAuthenticated);

  useEffect(() => {
    if (auth0User) {
      dispatch(updateAuth0User(auth0User));
      dispatch(
        setIsUserAdmin(
          auth0User?.['https://api.focusbear.io/roles'].includes(
            USER_ROLES.ADMIN
          )
        )
      );
      const syncKey = `${LOCAL_STORAGE.SYNC_PLATFORM_COURSES}_${auth0User.sub}`;
      if (
        auth0User?.['https://dashboard.focusbear.io/first_login'] &&
        !getLocalStorage(syncKey)
      ) {
        dispatch(syncPlatformCourses());
        updateLocalStorage(syncKey, LOCAL_STORAGE_TRUE);
      }
    }
  }, [auth0User, dispatch]);

  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      resetPostHogToAnonymous();
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    errorMessage && navigate(ROUTES.ERROR);
  }, [errorMessage, navigate]);

  useLayoutEffect(() => {
    if (
      isAuthenticated &&
      !authenticating &&
      location.pathname === ROUTES.LOGIN
    ) {
      const redirectRoute = checkIfRedirectNeeded();

      navigate(redirectRoute, { replace: true });
    }
  }, [isAuthenticated, authenticating, auth0User, navigate]);

  return (
    <Suspense
      fallback={<Loading icon={<></>} title={randomLoadingMessage()} />}>
      <Routes>
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoutes
              authenticating={authenticating}
              isAuthenticated={isAuthenticated}
            />
          }>
          {protectedRoutes.map((route) => {
            const { Component, index, path, children } = route;
            const routeKey = `${path ?? 'index'}/protected`;
            const childRoutes = children?.map((child) =>
              child.index ? (
                <Route key='index' index element={<child.Component />} />
              ) : (
                <Route
                  key={child.path}
                  path={child.path}
                  element={<child.Component />}
                />
              )
            );

            if (index) {
              return <Route key={routeKey} index element={<Component />} />;
            }

            if (childRoutes && childRoutes.length > 0) {
              return (
                <Route key={routeKey} path={path} element={<Component />}>
                  {childRoutes}
                </Route>
              );
            }

            return <Route key={routeKey} path={path} element={<Component />} />;
          })}
        </Route>
        <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
        <Route path={ROUTES.ERROR} element={<ErrorPage />} />
        <Route path={ROUTES.FORBIDDEN} element={<ForbiddenPage />} />
        <Route path={ROUTES.LOGIN} element={<AuthPage />} />
        {publicRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
      </Routes>
    </Suspense>
  );
};

export default withAuth0(RouteManager);
